import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

// ── Role definitions ────────────────────────────────────────────────────────
// Roles with their access levels (higher index = more access)
const ROLE_HIERARCHY = {
  iShip_admin: 100,
  shore_admin: 90,
  technical_manager: 80,
  fleet_superintendent: 70,
  procurement_officer: 60,
  chief_engineer: 50,
  second_engineer: 40,
  electrical_officer: 35,
  third_engineer: 30,
  bosun: 20,
};

// Pages accessible by role
const PAGE_PERMISSIONS = {
  dashboard: ['chief_engineer', 'second_engineer', 'third_engineer', 'electrical_officer', 'bosun', 'fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'],
  workOrders: ['chief_engineer', 'second_engineer', 'third_engineer', 'electrical_officer', 'bosun', 'fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'],
  maintenancePlan: ['chief_engineer', 'second_engineer', 'electrical_officer', 'fleet_superintendent', 'technical_manager', 'shore_admin', 'iShip_admin'],
  workPlans: ['chief_engineer', 'second_engineer', 'fleet_superintendent', 'technical_manager', 'shore_admin', 'iShip_admin'],
  inventory: ['chief_engineer', 'second_engineer', 'third_engineer', 'electrical_officer', 'bosun', 'fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'],
  purchasing: ['chief_engineer', 'second_engineer', 'procurement_officer', 'fleet_superintendent', 'technical_manager', 'shore_admin', 'iShip_admin'],
  reports: ['chief_engineer', 'second_engineer', 'fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'],
  miscellaneous: ['chief_engineer', 'second_engineer', 'electrical_officer', 'fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'],
  users: ['shore_admin', 'iShip_admin'],
  certificates: ['chief_engineer', 'second_engineer', 'electrical_officer', 'fleet_superintendent', 'technical_manager', 'shore_admin', 'iShip_admin'],
  notices: ['chief_engineer', 'second_engineer', 'third_engineer', 'electrical_officer', 'bosun', 'fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'],
  systemSettings: ['shore_admin', 'iShip_admin'],
};

// ── Auth Provider ────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [vessel, setVessel] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true); // starts true — no flash
  const [authChecked, setAuthChecked] = useState(false);

  // ── Validate token on mount ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('marisync_token');

    if (!token) {
      setLoading(false);
      setAuthChecked(true);
      return;
    }

    // Set user to "loading" state immediately (no token = immediate redirect)
    setLoading(true);

    api.get('/auth/me')
      .then(res => {
        if (cancelled) return;
        setUser(res.data.user);
        setVessel(res.data.vessel);
        setTenant(res.data.tenant);
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem('marisync_token');
        setUser(null);
        setVessel(null);
        setTenant(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setAuthChecked(true);
        }
      });

    return () => { cancelled = true; };
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (username, password) => {
    const res = await api.post('/auth/login', { username, password });
    const { token, user: loginUser } = res.data;

    localStorage.setItem('marisync_token', token);
    setUser(loginUser);

    // Fetch full user data with vessel/tenant
    const me = await api.get('/auth/me');
    setUser(me.data.user);
    setVessel(me.data.vessel);
    setTenant(me.data.tenant);

    return res.data;
  }, []);

  // ── Logout ──────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem('marisync_token');
    setUser(null);
    setVessel(null);
    setTenant(null);
    setAuthChecked(false);
    // Force navigate to login
    window.location.href = '/login';
  }, []);

  // ── Role checks ─────────────────────────────────────────────────────────────
  const hasRole = useCallback((role) => {
    if (!user) return false;
    const userLevel = ROLE_HIERARCHY[user.role] ?? 0;
    const checkLevel = ROLE_HIERARCHY[role] ?? 0;
    return userLevel >= checkLevel;
  }, [user]);

  const hasAnyRole = useCallback((roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  }, [user]);

  const canAccessPage = useCallback((pageKey) => {
    if (!user) return false;
    const allowed = PAGE_PERMISSIONS[pageKey];
    if (!allowed) return true; // unknown pages: allow
    return allowed.includes(user.role);
  }, [user]);

  const isShoreUser = useCallback(() => {
    if (!user) return false;
    return ['fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'].includes(user.role);
  }, [user]);

  const isVesselUser = useCallback(() => {
    if (!user) return false;
    return !isShoreUser();
  }, [user, isShoreUser]);

  return (
    <AuthContext.Provider value={{
      user, vessel, tenant, loading, authChecked,
      login, logout,
      hasRole, hasAnyRole, canAccessPage, isShoreUser, isVesselUser,
      ROLE_HIERARCHY,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
